import { Injectable, InjectionToken, inject, isDevMode } from '@angular/core';

const KB_URL =
  'https://script.google.com/a/macros/linkdigital.com.au/s/AKfycbz3ZzVe5Ir6929siOn5deYwKPUmErmOrO3I9B1NLiGvodVTWz1c5Z93TlY86OZnQEoZ/exec';

const urls = {
  dev: {
    authentication: 'https://w8q3jmltoa.execute-api.ap-southeast-2.amazonaws.com/dev',
    records: 'https://0lzmcdas0i.execute-api.ap-southeast-2.amazonaws.com/dev',
    clients: 'https://2nfvnzupy1.execute-api.ap-southeast-2.amazonaws.com/dev',
    products: 'https://zuj05lmm57.execute-api.ap-southeast-2.amazonaws.com/dev',
    agreements: 'https://6vhmx4z0nc.execute-api.ap-southeast-2.amazonaws.com/dev',
    documents: 'https://6vhmx4z0nc.execute-api.ap-southeast-2.amazonaws.com/dev',
    jira: 'https://whp5voc0d5.execute-api.ap-southeast-2.amazonaws.com/dev',
    resources: 'https://u1sdvq8duk.execute-api.ap-southeast-2.amazonaws.com/dev',
    resource_requests: 'https://tgckszyi9f.execute-api.ap-southeast-2.amazonaws.com/dev',
    projects: 'https://8tp1qrn6sh.execute-api.ap-southeast-2.amazonaws.com/dev',
    mail: 'https://82r63x24d8.execute-api.ap-southeast-2.amazonaws.com/dev',
    knowledgebase: KB_URL,
    documentation_export: 'https://y12lizr9k3.execute-api.ap-southeast-2.amazonaws.com/dev',
  },
  prod: {
    authentication: 'https://vhkt8b2gze.execute-api.ap-southeast-2.amazonaws.com/prod',
    records: 'https://ac6azhifvl.execute-api.ap-southeast-2.amazonaws.com/prod',
    clients: 'https://rm1fia79qc.execute-api.ap-southeast-2.amazonaws.com/prod',
    products: 'https://bmnqngj5g9.execute-api.ap-southeast-2.amazonaws.com/prod',
    agreements: 'https://gpjr88s86f.execute-api.ap-southeast-2.amazonaws.com/prod',
    documents: 'https://gpjr88s86f.execute-api.ap-southeast-2.amazonaws.com/prod',
    jira: 'https://ohn7jhcv6c.execute-api.ap-southeast-2.amazonaws.com/prod',
    resources: 'https://z0hn1p7upb.execute-api.ap-southeast-2.amazonaws.com/prod',
    resource_requests: 'https://k6ga48cv1m.execute-api.ap-southeast-2.amazonaws.com/prod',
    projects: 'https://odo5nqzc19.execute-api.ap-southeast-2.amazonaws.com/prod',
    mail: 'https://14oanuom08.execute-api.ap-southeast-2.amazonaws.com/prod',
    knowledgebase: KB_URL,
    documentation_export: 'https://y12lizr9k3.execute-api.ap-southeast-2.amazonaws.com/dev',
  },
};

function url(service: string): string {
  const env = inject(LCM_ENV);
  const collection = urls[env];
  if (collection) {
    return collection[service];
  }
  return `/${env}/${service}`;
}

const isDev = () => isDevMode();

export const LCM_ENV = new InjectionToken<string>('Environment for the app', {
  factory: () => (isDev() ? 'dev' : 'prod'),
});

@Injectable({ providedIn: 'root' })
export class Lambda {
  private projectsEndpoint: string = url('projects');
  private resourcesEndpoint: string = url('resources');
  private clientEndpoint: string = url('clients');
  private productEndpoint: string = url('products');
  private agreementEndpoint: string = url('agreements');
  private authenticationEndpoint: string = url('authentication');
  private documentsEndpoint: string = url('documents');
  private recordEndpoint: string = url('records');
  private jiraEndpoint: string = url('jira');
  private mailEndpoint: string = url('mail');
  private kbEndpoint: string = url('knowledgebase');
  private projectDocExport: string = url('documentation_export');

  private productUri = 'opportunities';
  private agreementUri = 'agreements';
  private authenticationUri = 'authentication';
  private documentsUri = 'documents';
  private recordUri = 'get';
  private jiraUri = 'report';
  private jiraCsvUri = 'report/csv';
  private userUri = 'users';

  getKnowledgeBaseLink(): URL {
    const url = new URL(this.kbEndpoint);
    url.searchParams.append('URL', location.href);
    return url;
  }

  getClientsUrl(): string {
    return `${this.clientEndpoint}`;
  }

  getMailUrl(): string {
    return `${this.mailEndpoint}`;
  }

  getResourcesUrl(): string {
    return `${this.resourcesEndpoint}`;
  }

  getProjectsUrl(): string {
    return `${this.projectsEndpoint}`;
  }

  getProductsUrl(): string {
    return `${this.productEndpoint}/${this.productUri}`;
  }

  getAgreementsUrl(): string {
    return `${this.agreementEndpoint}/${this.agreementUri}`;
  }

  getRecordsUrl(): string {
    return `${this.recordEndpoint}/${this.recordUri}`;
  }

  getJiraUrl(): string {
    return `${this.jiraEndpoint}/${this.jiraUri}`;
  }

  getJiraCsvUrl(): string {
    return `${this.jiraEndpoint}/${this.jiraCsvUri}`;
  }

  getSigninCallbackUrl(): string {
    return `${this.authenticationEndpoint}/${this.authenticationUri}/callback/google`;
  }

  getUsersUrl(): string {
    return `${this.authenticationEndpoint}/${this.userUri}`;
  }

  getDocumentsUrl(): string {
    return `${this.documentsEndpoint}/${this.documentsUri}`;
  }

  getProjectDocumentationExportUrl(projectId: string): string {
    return `${this.projectDocExport}/report/documentation/${projectId}`;
  }

  isDev = isDev;
}
